import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiDocumentSource } from '@izimi/api';
import { chunkArray } from '@izimi/utils';
import { isCommunicationDetails, isDocument } from './helpers';
import SharedQueryKeys from './SharedQueryKeys';
export const sharedQueryKeys = new SharedQueryKeys();
export const useInvalidateSharedItemsCache = () => {
    const queryClient = useQueryClient();
    const invalidateSharedItemsCache = useCallback(() => {
        return queryClient.invalidateQueries({
            queryKey: sharedQueryKeys.getSharedItems(),
        });
    }, [queryClient]);
    return invalidateSharedItemsCache;
};
export const useOptimisticSharedItemsDocumentsCopyCacheUpdate = () => {
    const queryClient = useQueryClient();
    const onMutate = useCallback(async ({ communicationId, documentIds }) => {
        await queryClient.cancelQueries({ queryKey: sharedQueryKeys.getSharedItems() });
        const previousSharedItemsQueries = queryClient.getQueriesData({ queryKey: sharedQueryKeys.getSharedItems() });
        previousSharedItemsQueries?.forEach(([queryKey, data]) => {
            if (!data)
                return;
            const updatedData = {
                ...data,
                pages: data.pages.map(page => ({
                    ...page,
                    items: page.items.map((item) => {
                        if (isCommunicationDetails(item) && item.id === communicationId) {
                            return {
                                ...item,
                                documents: item.documents.map((document) => {
                                    if (documentIds.includes(document.documentId)) {
                                        return { ...document, isAvailable: false };
                                    }
                                    return document;
                                }),
                            };
                        }
                        return item;
                    }),
                })),
            };
            queryClient.setQueryData(queryKey, updatedData);
        });
        return { previousSharedItemsQueries };
    }, [queryClient]);
    const onError = useCallback(async (_, __, context) => {
        if (context?.previousSharedItemsQueries) {
            context.previousSharedItemsQueries.forEach(([queryKey, data]) => {
                if (!data)
                    return;
                queryClient.setQueryData(queryKey, data);
            });
        }
    }, [queryClient]);
    return {
        onMutate,
        onError,
    };
};
export const useOptimisticSharedItemsCommunicationSeenCacheUpdate = () => {
    const queryClient = useQueryClient();
    const markSharedItemCommunicationAsSeen = useCallback(async ({ communicationId }) => {
        const previousSharedItemsQueries = queryClient.getQueriesData({ queryKey: sharedQueryKeys.getSharedItems() });
        previousSharedItemsQueries?.forEach(([queryKey, data]) => {
            if (!data)
                return;
            let updatedUnseenCommunication = 0;
            let updatedExpiringUnseenCommunication = 0;
            const updatedData = {
                ...data,
                pages: data.pages.map(page => ({
                    ...page,
                    items: page.items.map((item) => {
                        if (isCommunicationDetails(item) && item.id === communicationId) {
                            if (item.isSeen) {
                                return item;
                            }
                            if (item._collection === 'EXPIRING_COMMUNICATION_DETAILS') {
                                updatedExpiringUnseenCommunication += 1;
                            }
                            if (item._collection === 'NOT_EXPIRING_COMMUNICATION_DETAILS') {
                                updatedUnseenCommunication += 1;
                            }
                            return {
                                ...item,
                                isSeen: true,
                            };
                        }
                        return item;
                    }),
                })).map(page => ({
                    ...page,
                    metadata: {
                        ...page.metadata,
                        ...page.metadata?.numberUnseenCommunications && { numberUnseenCommunications: (page.metadata.numberUnseenCommunications ?? 0) - updatedUnseenCommunication },
                        ...page.metadata?.numberUnseenExpiringCommunications && { numberUnseenExpiringCommunications: (page.metadata.numberUnseenExpiringCommunications ?? 0) - updatedExpiringUnseenCommunication },
                    },
                })),
            };
            if (updatedUnseenCommunication === 0 &&
                updatedExpiringUnseenCommunication === 0) {
                return;
            }
            queryClient.setQueryData(queryKey, updatedData);
        });
        return { previousSharedItemsQueries };
    }, [queryClient]);
    return {
        markSharedItemCommunicationAsSeen,
    };
};
export const useOptimisticSharedItemsDocumentAcceptRejectCacheUpdate = () => {
    const queryClient = useQueryClient();
    const onMutate = useCallback(async ({ acceptedDocuments, rejectedDocuments }) => {
        await queryClient.cancelQueries({ queryKey: sharedQueryKeys.getSharedItems() });
        const previousSharedItemsQueries = queryClient.getQueriesData({ queryKey: sharedQueryKeys.getSharedItems() });
        const processedDocumentIds = [
            ...(acceptedDocuments ?? [])?.map(document => document.documentId),
            ...(rejectedDocuments ?? [])?.map(document => document.documentId),
        ];
        previousSharedItemsQueries?.forEach(([queryKey, data]) => {
            if (!data)
                return;
            let removedItemsCount = 0;
            const items = data.pages
                .flatMap(page => page.items)
                .filter((item) => {
                const shouldKeep = !isDocument(item) ||
                    item.source === ApiDocumentSource.Shared ||
                    !processedDocumentIds.includes(item.id);
                if (!shouldKeep) {
                    removedItemsCount += 1;
                }
                return shouldKeep;
            })
                .map((item) => {
                if (!processedDocumentIds.includes(item.id) || !isDocument(item)) {
                    return item;
                }
                return {
                    ...item,
                    state: 'ACTIVE',
                };
            });
            if (items.length === 0) {
                queryClient.setQueryData(queryKey, {
                    pageParams: [],
                    pages: [
                        {
                            items: [],
                            total: 0,
                        },
                    ],
                });
                return;
            }
            queryClient.setQueryData(queryKey, {
                pageParams: data?.pageParams,
                pages: chunkArray(items, 15).map(items => ({
                    ...data.pages[0],
                    items,
                    total: (data.pages[0]?.total ?? 0) - removedItemsCount,
                })),
            });
        });
        return { previousSharedItemsQueries };
    }, [queryClient]);
    const onError = useCallback(async (_, __, context) => {
        if (context?.previousSharedItemsQueries) {
            context.previousSharedItemsQueries.forEach(([queryKey, data]) => {
                if (!data)
                    return;
                queryClient.setQueryData(queryKey, data);
            });
        }
    }, [queryClient]);
    return {
        onMutate,
        onError,
    };
};
export const useOptimisticSharedItemsCommunicationsDeleteCacheUpdate = () => {
    const queryClient = useQueryClient();
    const onMutate = useCallback(async ({ communicationIds }) => {
        await queryClient.cancelQueries({ queryKey: sharedQueryKeys.getSharedItems() });
        const previousSharedItemsQueries = queryClient.getQueriesData({ queryKey: sharedQueryKeys.getSharedItems() });
        previousSharedItemsQueries?.forEach(([queryKey, data]) => {
            if (!data)
                return;
            let removedItemsCount = 0;
            let removedUnseenCommunicationCount = 0;
            let removedExpiringUnseenCommunicationCount = 0;
            const items = data.pages
                .flatMap(page => page.items)
                .filter((item) => {
                if (!isCommunicationDetails(item) || !communicationIds.includes(item.id)) {
                    return true;
                }
                if (item._collection === 'NOT_EXPIRING_COMMUNICATION_DETAILS') {
                    removedUnseenCommunicationCount += 1;
                }
                if (item._collection === 'EXPIRING_COMMUNICATION_DETAILS') {
                    removedExpiringUnseenCommunicationCount += 1;
                }
                removedItemsCount += 1;
                return false;
            });
            if (items.length === 0) {
                queryClient.setQueryData(queryKey, {
                    pageParams: [],
                    pages: [
                        {
                            items: [],
                            total: 0,
                        },
                    ],
                });
                return;
            }
            queryClient.setQueryData(queryKey, {
                pageParams: data?.pageParams,
                pages: chunkArray(items, 15).map(items => ({
                    ...data.pages[0],
                    items,
                    total: (data.pages[0]?.total ?? 0) - removedItemsCount,
                    metadata: {
                        ...data.pages[0]?.metadata,
                        ...data.pages[0]?.metadata?.numberUnseenCommunications && {
                            numberUnseenCommunications: (data.pages[0].metadata.numberUnseenCommunications ?? 0) - removedUnseenCommunicationCount,
                        },
                        ...data.pages[0]?.metadata?.numberUnseenExpiringCommunications && {
                            numberUnseenExpiringCommunications: (data.pages[0].metadata.numberUnseenExpiringCommunications ?? 0) - removedExpiringUnseenCommunicationCount,
                        },
                    },
                })),
            });
        });
        return { previousSharedItemsQueries };
    }, [queryClient]);
    const onError = useCallback(async (_, __, context) => {
        if (context?.previousSharedItemsQueries) {
            context.previousSharedItemsQueries.forEach(([queryKey, data]) => {
                if (!data)
                    return;
                queryClient.setQueryData(queryKey, data);
            });
        }
    }, [queryClient]);
    const onSuccess = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: sharedQueryKeys.getSharedItems() });
    }, [queryClient]);
    return {
        onMutate,
        onError,
        onSuccess,
    };
};
